import { auth } from '../firebase.js';

const callableCache = new Map();

const FUNCTION_ENDPOINT_MAP = {
  createOrOpenDefaultCareTeamConversation: 'httpCreateOrOpenDefaultCareTeamConversation',
  createCustomChildConversation: 'httpCreateCustomChildConversation',
  listChildConversations: 'httpListChildConversations',
  getConversationById: 'httpGetConversationById',
  updateConversation: 'httpUpdateConversation',
  archiveConversation: 'httpArchiveConversation',
  getConversationMessages: 'httpGetConversationMessages',
  sendConversationMessage: 'httpSendConversationMessage',
  markConversationMessageRead: 'httpMarkConversationMessageRead',
  promoteChatMessageToLifelog: 'httpPromoteChatMessageToLifelog',
  promoteChatMessagesToLifelog: 'httpPromoteChatMessagesToLifelog',
  getChatMessagePromotionStates: 'httpGetChatMessagePromotionStates',
  registerNotificationToken: 'httpRegisterNotificationToken',
  unregisterNotificationToken: 'httpUnregisterNotificationToken',
};

const getProjectId = () => auth.app?.options?.projectId || process.env.REACT_APP_FIREBASE_PROJECT_ID || 'lifelog-test-aa7d6';

const getFunctionBaseUrl = () => {
  const projectId = getProjectId();
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

const getAuthToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Authentication required');
  }

  return currentUser.getIdToken();
};

export const normalizeCallableErrorCode = (value) => String(value || 'internal')
  .replace(/^functions\//i, '')
  .trim()
  .toLowerCase()
  .replace(/_/g, '-');

const callHttpMessagingFunction = async (name, payload = {}) => {
  const endpointName = FUNCTION_ENDPOINT_MAP[name] || name;
  const token = await getAuthToken();
  const response = await fetch(`${getFunctionBaseUrl()}/${endpointName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ data: payload }),
  });

  let body = null;
  try {
    body = await response.json();
  } catch (error) {
    body = null;
  }

  if (!response.ok) {
    const message = body?.error?.message || body?.message || `Request failed with status ${response.status}`;
    const error = new Error(message);
    error.code = normalizeCallableErrorCode(body?.error?.status || response.statusText);
    throw error;
  }

  if (body?.error) {
    const error = new Error(body.error.message || 'Messaging function failed');
    error.code = normalizeCallableErrorCode(body.error.status);
    throw error;
  }

  return body?.data || {};
};

export const callMessagingFunction = async (name, payload = {}) => {
  const callable = callableCache.get(name) || callHttpMessagingFunction;
  if (!callableCache.has(name)) {
    callableCache.set(name, callable);
  }
  return callable(name, payload);
};

const normalizeTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.toMillis === 'function') return new Date(value.toMillis());
  return null;
};

const normalizeReadBy = (readBy = {}) => {
  const normalized = {};
  Object.entries(readBy || {}).forEach(([userId, timestamp]) => {
    normalized[userId] = normalizeTimestamp(timestamp);
  });
  return normalized;
};

const normalizeLastMessage = (lastMessage = null) => {
  if (!lastMessage) return null;
  return {
    ...lastMessage,
    timestamp: normalizeTimestamp(lastMessage.timestamp),
  };
};

export const normalizeConversation = (conversation = null) => {
  if (!conversation) return null;
  const participants = conversation.participants || conversation.participantIds || [];
  return {
    ...conversation,
    id: conversation.id || conversation.conversationId || null,
    participants: Array.isArray(participants) ? [...new Set(participants)] : [],
    participantIds: Array.isArray(participants) ? [...new Set(participants)] : [],
    createdAt: normalizeTimestamp(conversation.createdAt),
    updatedAt: normalizeTimestamp(conversation.updatedAt),
    lastMessage: normalizeLastMessage(conversation.lastMessage),
  };
};

export const normalizeMessage = (message = null) => {
  if (!message) return null;
  return {
    ...message,
    id: message.id || message.messageId || null,
    createdAt: normalizeTimestamp(message.createdAt),
    updatedAt: normalizeTimestamp(message.updatedAt),
    deletedAt: normalizeTimestamp(message.deletedAt),
    readBy: normalizeReadBy(message.readBy),
  };
};
